import React from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
// Import Shared Components
import { IconButton, Input, Dropdown } from 'shared-components';
// Import Shared Utils
import { getCountryCode } from 'shared-components/src/utils/formatCountry';
import { PROVINCES, STATES } from 'shared-components/src/utils/provinces';
// Import Global Components
import Title from '../../../../components/Title';
// Import Global Layouts
import InputGroup from '../../../../layouts/InputGroup';
import InputRow from '../../../../layouts/InputRow';
// Import Global Utils
import {
  subscriberDigits,
  checkSelectedState,
  stateKeys,
  provinceKeys,
} from '../../../../utils/profile';
import { errorMessage } from '../../../../utils/validationHandlers';
// Import Component Styles
import { FieldError } from '../../../Auth/styles';
import { TitleWrapper } from './styles';

const PHONE_TYPES = ['mobile', 'home', 'work'];

const ProfileForm = ({
  disabled,
  addressReadOnly,
  iconType,
  onClick,
  onChange,
  onBlur,
  cardholder,
  storedPhone = null,
  validationErrors = {},
}) => {
  // create Intl instance
  const intl = useIntl();
  const page = 'profile';

  // Props checking an input as it is left and tying it to the message rendered
  // under it by fieldError.
  const errorProps = (field, id) => ({
    onBlur: () => onBlur(field),
    className: validationErrors[field] ? 'is-invalid' : '',
    'aria-invalid': Boolean(validationErrors[field]),
    'aria-describedby': validationErrors[field] ? `${id}-error` : undefined,
  });
  const fieldError = (field, id) => validationErrors[field] && (
    <FieldError id={`${id}-error`} className="invalid-feedback">
      {errorMessage(intl, validationErrors[field])}
    </FieldError>
  );

  // The flat `phone` only carries mobile, so a number filed as home or work
  // exists only in the typed `phones` list.
  const phones = Array.isArray(cardholder.phones) ? cardholder.phones : [];
  // Paired against the SAVED number, never the field being typed in: matching the
  // live value would unpair mid-edit, dropping the type off the label and listing
  // the same number again as a read-only row.
  const referencePhone = storedPhone ?? cardholder.phone;
  const primaryPhone = phones.find(p => subscriberDigits(p.number) === subscriberDigits(referencePhone));
  const otherPhones = phones.filter(p => p !== primaryPhone);
  // A type another number already holds is withheld: both processors key phones by
  // type, so two entries sharing one silently collapse into the last written —
  // E6 deletes the entry whose type the request no longer carries, and Galileo has
  // exactly one slot per type.
  const takenTypes = otherPhones.map(p => p.type);
  const availableTypes = PHONE_TYPES.filter(type => !takenTypes.includes(type));
  // The draft wins once the cardholder picks; otherwise the stored type seeds it.
  // Anything not on offer (the processor also emits contact/fax) resolves to null
  // so the dropdown prompts instead of silently relabelling it.
  const draftPhoneType = cardholder.phone_type ?? primaryPhone?.type;
  const selectedPhoneType = availableTypes.includes(draftPhoneType) ? draftPhoneType : null;

  // The mailing address is the program's to allow: where it is maintained upstream
  // the fields stay read-only in edit mode too, and a profile update that altered
  // them would be refused by the API anyway.
  const addressDisabled = disabled || addressReadOnly;
  // `disabled` is an affordance, not a control — devtools can drop the attribute.
  // The handler refuses the change as well, so a field these inputs control snaps
  // back to its stored value instead of taking an edit.
  const onAddressChange = addressDisabled ? () => {} : onChange;


  const getSelected = () => {
    if (
      checkSelectedState(cardholder, stateKeys, '840') ||
      checkSelectedState(cardholder, provinceKeys, '124')
    )
      return cardholder.state;

    return null;
  };

  return (
    <>
      <TitleWrapper>
        <Title
          title={intl.messages['profile-personal-info-title']}
          titleFor="personal-info"
          page="profile"
        />
        <IconButton iconType={iconType} onClick={onClick} />
      </TitleWrapper>
      <InputRow>
        <InputGroup
          label={intl.messages[`${page}-firstName-label`]}
          labelFor="firstName"
          page={page}
          required
        >
          <Input
            value={cardholder.first_name}
            id="profile-firstName"
            autocomplete="first-name"
            disabled="disabled"
            maxLength={40}
            required
          />
        </InputGroup>
        <InputGroup
          label={intl.messages[`${page}-middleName-label`]}
          labelFor="middleName"
          page={page}
        >
          <Input
            value={cardholder.middle_name}
            id="profile-middleName"
            autocomplete="middle-name"
            disabled="disabled"
            maxLength={40}
          />
        </InputGroup>
        <InputGroup
          label={intl.messages[`${page}-lastName-label`]}
          labelFor="lastName"
          page={page}
          required
        >
          <Input
            value={cardholder.last_name}
            id="profile-lastName"
            autocomplete="last-name"
            disabled="disabled"
            maxLength={40}
            required
          />
        </InputGroup>
        <InputGroup
          label={intl.messages[`${page}-dateOfBirth-label`]}
          labelFor="dateOfBirth"
          page={page}
        >
          <Input
            value={cardholder.date_of_birth}
            id="profile-dateOfBirth"
            disabled="disabled"
          />
        </InputGroup>
      </InputRow>
      <InputRow>
        <InputGroup
          label={intl.messages[`${page}-email-label`]}
          labelFor="email"
          page={page}
        >
          <Input
            value={cardholder.email}
            id="profile-email"
            autocomplete="email"
            onChange={e => onChange({ key: 'email', value: e.target.value })}
            disabled={disabled}
            type="email"
            {...errorProps('email', 'profile-email')}
          />
          {fieldError('email', 'profile-email')}
        </InputGroup>
      </InputRow>
      {/* One number, and the type it is filed under. The portal neither adds nor
          removes numbers — anything else the processor holds travels untouched in
          the payload, which is why a type already in use is withheld here. */}
      <InputRow>
        <InputGroup
          label={intl.messages[`${page}-phoneType-label`]}
          labelFor="phoneType"
          page={page}
          required
        >
          <Dropdown
            required
            selected={selectedPhoneType}
            options={availableTypes.map(type => ({
              key: type,
              val: intl.messages[`${page}-phoneType-${type}`],
            }))}
            updateItem={value => onChange({ key: 'phone_type', value })}
            id="profile-phone-type"
            disabled={disabled}
          />
        </InputGroup>
        <InputGroup
          label={intl.messages[`${page}-phone-label`]}
          labelFor="phone"
          page={page}
          required
        >
          <Input
            value={cardholder.phone}
            id="profile-phone"
            autocomplete="phone"
            onChange={e => onChange({ key: 'phone', value: e.target.value })}
            disabled={disabled}
            required
            type="tel"
            maxLength={16}
            {...errorProps('phone', 'profile-phone')}
          />
          {fieldError('phone', 'profile-phone')}
        </InputGroup>
      </InputRow>


      <TitleWrapper>
        <Title
          title={intl.messages['profile-personal-mailing-title']}
          titleFor="personal-mailing"
          page="profile"
        />
      </TitleWrapper>
      <InputRow>
        <InputGroup
          label={intl.messages[`${page}-addressLine1-label`]}
          labelFor="addressLine1"
          page={page}
          required
        >
          <Input
            required
            value={cardholder.address1}
            id="profile-addressLine1"
            autocomplete="addressLine1"
            onChange={e => onAddressChange({ key: 'address1', value: e.target.value })}
            disabled={addressDisabled}
            {...errorProps('address1', 'profile-addressLine1')}
          />
          {fieldError('address1', 'profile-addressLine1')}
        </InputGroup>
        <InputGroup
          label={intl.messages[`${page}-addressLine2-label`]}
          labelFor="addressLine2"
          page={page}
        >
          <Input
            value={cardholder.address2}
            id="profile-addressLine2"
            autocomplete="addressLine2"
            onChange={e => onAddressChange({ key: 'address2', value: e.target.value })}
            disabled={addressDisabled}
            {...errorProps('address2', 'profile-addressLine2')}
          />
          {fieldError('address2', 'profile-addressLine2')}
        </InputGroup>
      </InputRow>

      <InputRow>
        <InputGroup
          label={intl.messages[`${page}-country-label`]}
          labelFor="country"
          page={page}
          required
        >
          <Dropdown
            required
            options={[
              { key: '124', val: 'CANADA' },
              { key: '840', val: 'USA' },
            ]}
            selected={getCountryCode(cardholder.country)}
            id="profile-country"
            disabled="disabled"
          />
        </InputGroup>
        <InputGroup
          label={intl.messages[`${page}-province-label`]}
          labelFor="province"
          page={page}
          required
        >
          <Dropdown
            required
            selected={getSelected()}
            options={cardholder.country === '840' ? STATES : PROVINCES}
            // The shared Dropdown only stops the list from opening when disabled;
            // its items still answer a click, so it goes through the gate too.
            updateItem={value => onAddressChange({ key: 'state', value })}
            id="profile-province"
            disabled={addressDisabled}
          />
        </InputGroup>
      </InputRow>

      <InputRow>
        <InputGroup
          required
          label={intl.messages[`${page}-city-label`]}
          labelFor="city"
          page={page}
        >
          <Input
            required
            value={cardholder.city}
            id="profile-city"
            autocomplete="city"
            onChange={e => onAddressChange({ key: 'city', value: e.target.value })}
            disabled={addressDisabled}
            {...errorProps('city', 'profile-city')}
          />
          {fieldError('city', 'profile-city')}
        </InputGroup>
        <InputGroup
          required
          label={intl.messages[`${page}-postalCode-label`]}
          labelFor="postalCode"
          page={page}
        >
          <Input
            required
            value={cardholder.postal_code}
            id="profile-postalCode"
            autocomplete="postalCode"
            onChange={e =>
              onAddressChange({ key: 'postal_code', value: e.target.value })}
            disabled={addressDisabled}
            min={5}
            maxLength={10}
            {...errorProps('postal_code', 'profile-postalCode')}
          />
          {fieldError('postal_code', 'profile-postalCode')}
        </InputGroup>
      </InputRow>
    </>
  );
};

ProfileForm.propTypes = {
  onClick: PropTypes.func.isRequired,
  iconType: PropTypes.string.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  cardholder: PropTypes.object.isRequired,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  disabled: PropTypes.bool,
  addressReadOnly: PropTypes.bool,
  storedPhone: PropTypes.string,
  // i18n keys from profileFieldError, keyed by cardholder field.
  validationErrors: PropTypes.objectOf(PropTypes.string),
};

ProfileForm.defaultProps = {
  disabled: false,
  addressReadOnly: false,
  onChange: () => {},
  onBlur: () => {},
  storedPhone: null,
  validationErrors: {},
};

export default ProfileForm;
